import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { BroadcastService } from '../broadcast.service';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import { BroadcastRequest, DrawerParams } from '../broadcast';
import { LookupService } from '../../../core/services/lookup.service';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import { LookupItem } from '../../../core/models/lookup.models';
import { DrawerService } from '../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CommonService } from '../../../shared/services/common.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { TimeZoneService } from '../../../core/services/timezone.service';

@Component({
  selector: 'app-broadcast-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzDatePickerModule,
    NzSwitchModule,
    NzSpinModule,
  ],
  templateUrl: './broadcast-form.component.html',
  styleUrl: './broadcast-form.component.scss',
})
export class BroadcastFormComponent implements OnInit, OnDestroy {
  drawerBroadcastParams: DrawerParams = { mode: 'CREATE' };

  broadcastForm!: FormGroup;
  loading$ = new BehaviorSubject<boolean>(false);
  broadcastTypes$ = new BehaviorSubject<LookupItem[]>([]);
  organizations$ = new BehaviorSubject<LookupItem[]>([]);
  organizationsLoading$ = new BehaviorSubject<boolean>(false);
  noOrganizationsFound = false;
  private searchOrganization$ = new Subject<string>();

  private destroy$ = new Subject<void>();

  readonly GENERAL_BROADCAST_TYPE_ID = 104;
  isGeneralBroadcastType = false;

  private broadcastTargetAudiences: {
    organizationId: number;
    organizationName?: string;
  }[] = [];

  constructor(
    private fb: FormBuilder,
    private broadcastService: BroadcastService,
    private lookupService: LookupService,
    private drawerService: DrawerService,
    private commonService: CommonService,
    private timeZoneService: TimeZoneService
  ) {}

  ngOnInit(): void {
    this.drawerService
      .getParams<DrawerParams>(DRAWER_IDS.BROADCAST)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerBroadcastParams = params;
        }
      });
    this.initForm();
    this.loadFormData();
    this.setupOrganizationSearch();
    this.setupBroadcastTypeChanges();

    if (
      this.drawerBroadcastParams.mode !== 'CREATE' &&
      this.drawerBroadcastParams.id
    ) {
      this.loadBroadcastData(this.drawerBroadcastParams.id);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.broadcastForm = this.fb.group(
      {
        broadcastTypeId: [null, Validators.required],
        title: [null, Validators.required],
        message: [null, Validators.required],
        isDraft: [true],
        startDateTime: [null],
        endDateTime: [null],
        targetOrganizations: [[], Validators.required],
      },
      {
        validators: this.validateDateRange(),
      }
    );

    this.updateDateFieldValidators();

    if (this.drawerBroadcastParams.mode === 'VIEW') {
      this.broadcastForm.disable();
    }
  }

  setupOrganizationSearch(): void {
    this.searchOrganization$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap(() => {
          this.organizationsLoading$.next(true);
          this.organizations$.next([]);
        }),
        switchMap(searchTerm =>
          this.lookupService.getLookupValue('Organization', searchTerm).pipe(
            finalize(() => {
              this.organizationsLoading$.next(false);
            })
          )
        )
      )
      .subscribe({
        next: response => {
          const selectedOrgIds =
            this.broadcastForm.get('targetOrganizations')?.value || [];
          const orgMap = new Map<number, LookupItem>();

          (response || []).forEach(org => orgMap.set(org.id, org));
          this.noOrganizationsFound = response.length === 0;

          this.broadcastTargetAudiences.forEach(audience => {
            if (
              !orgMap.has(audience.organizationId) &&
              audience.organizationName
            ) {
              orgMap.set(audience.organizationId, {
                id: audience.organizationId,
                value: audience.organizationName,
              });
            }
          });

          if (selectedOrgIds.length > 0) {
            const currentOrgs = this.organizations$.value || [];
            currentOrgs.forEach(org => {
              if (selectedOrgIds.includes(org.id) && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          }

          this.organizations$.next(Array.from(orgMap.values()));
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.organizationsLoading$.next(false);
          this.noOrganizationsFound = true;
          this.organizations$.next([]);
        },
      });
  }

  onOrganizationSearch(searchTerm: string): void {
    this.searchOrganization$.next(searchTerm);
  }

  loadFormData(): void {
    this.loading$.next(true);
    this.lookupService
      .getLookupValue('BroadcastNotificationType', '')
      .pipe(takeUntil(this.destroy$))
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: response => {
          if (response && response.length) {
            this.broadcastTypes$.next(response);
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
    this.loadOrganizations('');
  }

  loadOrganizations(searchTerm: string): void {
    this.organizationsLoading$.next(true);
    this.organizations$.next([]);

    this.lookupService
      .getLookupValue('Organization', searchTerm)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.organizationsLoading$.next(false);
        })
      )
      .subscribe({
        next: response => {
          const selectedOrgIds =
            this.broadcastForm.get('targetOrganizations')?.value || [];
          const orgMap = new Map<number, LookupItem>();

          this.noOrganizationsFound = response.length === 0;
          (response || []).forEach(org => orgMap.set(org.id, org));

          this.broadcastTargetAudiences.forEach(audience => {
            if (
              !orgMap.has(audience.organizationId) &&
              audience.organizationName
            ) {
              orgMap.set(audience.organizationId, {
                id: audience.organizationId,
                value: audience.organizationName,
              });
            }
          });

          if (selectedOrgIds.length > 0) {
            const currentOrgs = this.organizations$.value || [];
            currentOrgs.forEach(org => {
              if (selectedOrgIds.includes(org.id) && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          }

          this.organizations$.next(Array.from(orgMap.values()));
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.organizationsLoading$.next(false);
          this.noOrganizationsFound = true;
          this.organizations$.next([]);
        },
      });
  }

  loadBroadcastData(id: number): void {
    this.loading$.next(true);

    this.broadcastService
      .getBroadcastById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response && response.result) {
            const broadcast = response.result;

            this.broadcastTargetAudiences = (
              broadcast.broadcastTargetAudiences || []
            ).map(audience => ({
              organizationId: audience.organizationId,
              organizationName: audience.organizationName || '',
            }));

            const targetOrganizations =
              broadcast.broadcastTargetAudiences?.map(
                audience => audience.organizationId
              ) || [];

            const startDateTime = broadcast.startDateTime
              ? this.timeZoneService.convertToUserTimeZone(
                  broadcast.startDateTime
                )
              : null;
            const endDateTime = broadcast.endDateTime
              ? this.timeZoneService.convertToUserTimeZone(
                  broadcast.endDateTime
                )
              : null;

            this.broadcastForm.patchValue({
              broadcastTypeId: broadcast.broadcastTypeId,
              title: broadcast.title,
              message: broadcast.message,
              isDraft: broadcast.isDraft,
              startDateTime: startDateTime,
              endDateTime: endDateTime,
              targetOrganizations: targetOrganizations,
            });

            this.isGeneralBroadcastType =
              broadcast.broadcastTypeId === this.GENERAL_BROADCAST_TYPE_ID;
            this.updateDateFieldValidators();
            this.loadOrganizations('');
          }
          this.loading$.next(false);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
  }

  submitForm(): void {
    if (this.broadcastForm.invalid) {
      return;
    }

    this.loading$.next(true);
    const formValue = this.broadcastForm.value;
    const broadcastTargetAudiences = formValue.targetOrganizations.map(
      (orgId: number) => ({
        organizationId: orgId,
      })
    );

    const localStartDateTime = this.isGeneralBroadcastType
      ? null
      : formValue.startDateTime;
    const localEndDateTime = this.isGeneralBroadcastType
      ? null
      : formValue.endDateTime;

    const userTimezone = this.timeZoneService.getUserTimeZone();
    let utcStartDateTime = null;
    let utcEndDateTime = null;

    if (localStartDateTime) {
      utcStartDateTime = this.timeZoneService.convertSpecificTimezoneToUTC(
        localStartDateTime,
        userTimezone
      );
    }

    if (localEndDateTime) {
      utcEndDateTime = this.timeZoneService.convertSpecificTimezoneToUTC(
        localEndDateTime,
        userTimezone
      );
    }

    const broadcastRequest: BroadcastRequest = {
      broadcastTypeId: formValue.broadcastTypeId,
      title: formValue.title,
      message: formValue.message,
      isDraft: formValue.isDraft,
      startDateTime: utcStartDateTime ? utcStartDateTime.toISOString() : '',
      endDateTime: utcEndDateTime ? utcEndDateTime.toISOString() : '',
      broadcastTargetAudiences: broadcastTargetAudiences,
    };

    const request$ =
      this.drawerBroadcastParams.mode === 'CREATE'
        ? this.broadcastService.createBroadcast(broadcastRequest)
        : this.broadcastService.updateBroadcast(
            this.drawerBroadcastParams.id!,
            broadcastRequest
          );

    const successMessage =
      this.drawerBroadcastParams.mode === 'CREATE'
        ? 'Broadcast created successfully'
        : 'Broadcast updated successfully';

    request$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        finalize(() => {
          this.loading$.next(false);
          this.drawerService.closeDrawer(DRAWER_IDS.BROADCAST);
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', [successMessage]);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
  }

  cancel(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.BROADCAST);
  }

  switchToEditMode(): void {
    this.drawerBroadcastParams.mode = 'EDIT';
    this.drawerService.updateDrawerTitle(
      DRAWER_IDS.BROADCAST,
      'Edit Broadcast'
    );
    this.broadcastForm.enable();
  }

  trackByFn(index: number, item: LookupItem): number {
    return item.id;
  }

  setupBroadcastTypeChanges(): void {
    this.broadcastForm
      .get('broadcastTypeId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const wasGeneral = this.isGeneralBroadcastType;
        this.isGeneralBroadcastType = value === this.GENERAL_BROADCAST_TYPE_ID;

        if (!wasGeneral && this.isGeneralBroadcastType) {
          this.broadcastForm.get('startDateTime')?.setValue(null);
          this.broadcastForm.get('endDateTime')?.setValue(null);
        }

        this.updateDateFieldValidators();
      });
  }

  updateDateFieldValidators(): void {
    const startDateControl = this.broadcastForm.get('startDateTime');
    const endDateControl = this.broadcastForm.get('endDateTime');

    if (this.isGeneralBroadcastType) {
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
    } else {
      startDateControl?.setValidators([
        Validators.required,
        this.futureDateValidator(),
      ]);
      endDateControl?.setValidators(Validators.required);
    }

    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const inputDate = new Date(control.value);
      const inputDateOnly = new Date(inputDate);
      inputDateOnly.setHours(0, 0, 0, 0);

      return inputDateOnly < currentDate
        ? { pastDate: { value: control.value } }
        : null;
    };
  }

  validateDateRange(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      if (this.isGeneralBroadcastType) {
        return null;
      }

      const startDateControl = formGroup.get('startDateTime');
      const endDateControl = formGroup.get('endDateTime');

      if (
        !startDateControl ||
        !endDateControl ||
        !startDateControl.value ||
        !endDateControl.value
      ) {
        return null;
      }

      const startDate = new Date(startDateControl.value);
      const endDate = new Date(endDateControl.value);

      return endDate <= startDate ? { endDateBeforeStart: true } : null;
    };
  }
}
